import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Users, Award, Heart, ArrowRight, Sparkles, Lightbulb, Bot, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useBrandName } from '../context/SettingsContext';
import EditableText from '../components/EditableText';
import { IMAGES } from '../config/images';
import { BTN_PRIMARY } from '../config/brand';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import CircuitDivider from '../components/CircuitDivider';
import SectionHeader from '../components/SectionHeader';
import FeatureCard from '../components/FeatureCard';
import IconBadge from '../components/IconBadge';
import CtaBanner from '../components/CtaBanner';

const VALUES = [
  { icon: Target, variant: 'purple', title: 'Excellence', description: 'Programme pédagogique conçu par des experts et validé sur le terrain' },
  { icon: Users, variant: 'orange', title: 'Accessibilité', description: 'La robotique ouverte à tous, quel que soit le niveau de départ' },
  { icon: Award, variant: 'purple', title: 'Innovation', description: 'Méthodes actives, projets concrets et outils adaptés à chaque âge' },
  { icon: Heart, variant: 'orange', title: 'Passion', description: "Une équipe engagée qui transmet l'enthousiasme de la tech" },
];

const PARTNERSHIP_ITEMS = [
  { icon: Lightbulb, title: 'Méthode éprouvée', desc: "Programme testé et validé par des milliers d'élèves en France" },
  { icon: Bot, title: 'Robots de qualité', desc: "Kits robotiques conçus spécifiquement pour l'apprentissage scolaire" },
  { icon: GraduationCap, title: 'Formation continue', desc: 'Nos formateurs suivent régulièrement les dernières méthodes pédagogiques' },
];

const About = () => {
  const brandName = useBrandName();
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative pt-below-site-header pb-16 sm:pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(58,0,224,0.06)] via-white to-[rgba(250,176,58,0.08)]" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-[rgba(58,0,224,0.1)] rounded-full blur-3xl" aria-hidden="true" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-[rgba(250,176,58,0.14)] rounded-full blur-3xl" aria-hidden="true" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <Reveal animation="fade-up">
              <div>
                <p className="text-[#3a00e0] text-sm font-semibold uppercase tracking-wider mb-4 inline-flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                  <EditableText
                    editKey="about.hero.badge"
                    defaultValue="Notre histoire"
                    as="span"
                    className="text-[#3a00e0] text-sm font-semibold uppercase tracking-wider"
                  />
                </p>
                <EditableText
                  editKey="about.hero.title"
                  defaultValue={`À propos de ${brandName}`}
                  as="h1"
                  className="text-3xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-5 sm:mb-6 leading-tight tracking-tight"
                />
                <EditableText
                  editKey="about.hero.subtitle"
                  defaultValue="Ateliers de robotique et programmation pour les 6–18 ans sur la Côte d'Azur — un environnement bienveillant où chaque enfant progresse à son rythme."
                  as="p"
                  multiline
                  className="text-lg sm:text-xl text-slate-600 mb-5 leading-relaxed max-w-xl"
                />
                <EditableText
                  editKey="about.hero.body"
                  defaultValue="Depuis notre création, nous avons accompagné plus de 500 jeunes vers les compétences du numérique. Notre mission : rendre la robotique accessible, concrète et motivante pour tous."
                  as="p"
                  multiline
                  className="text-slate-600 mb-8 leading-relaxed max-w-xl"
                />
                <Link to="/reserver">
                  <Button size="lg" className={`${BTN_PRIMARY} w-full sm:w-auto`}>
                    <EditableText editKey="about.hero.cta" defaultValue="Rejoignez l'aventure" className="inline" />
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </Reveal>

            <Reveal animation="fade-in" delay={120} className="relative mt-4 lg:mt-0">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-[rgba(58,0,224,0.18)] shadow-2xl shadow-[0_20px_40px_-20px_rgba(58,0,224,0.2)] ring-1 ring-white/5">
                <img
                  src={IMAGES.about.workshop}
                  alt={`Ateliers robotique ${brandName}`}
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CircuitDivider />

      <section className="py-16 sm:py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Nos valeurs"
            eyebrowKey="about.values.eyebrow"
            title="Ce qui guide notre action"
            titleKey="about.values.title"
            subtitle="Des principes concrets au service de chaque élève"
            subtitleKey="about.values.subtitle"
            reveal
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((item, i) => (
              <Reveal key={item.title} animation="fade-up" delay={i * 80}>
                <FeatureCard
                  {...item}
                  size="lg"
                  titleKey={`about.values.${i + 1}.title`}
                  descriptionKey={`about.values.${i + 1}.text`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-14 sm:py-16 px-4 border-y border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              { value: '500+', label: 'Élèves formés', key: 'about.stats.1.label' },
              { value: '5', label: 'Centres', key: 'about.stats.2.label' },
              { value: '50+', label: 'Modules pédagogiques', key: 'about.stats.3.label' },
              { value: '98%', label: 'Parents satisfaits', key: 'about.stats.4.label' },
            ].map((stat, i) => (
              <Reveal key={stat.key} animation="scale-in" delay={i * 60}>
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-gradient-brand mb-1">
                    {stat.value}
                  </div>
                  <EditableText
                    editKey={stat.key}
                    defaultValue={stat.label}
                    as="p"
                    className="text-sm text-slate-600"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Partenariat"
            eyebrowKey="about.partner.eyebrow"
            title="Partenaire pédagogique Algora"
            titleKey="about.partner.title"
            subtitle={`${brandName} s'appuie sur Algora, entreprise spécialisée dans l'éducation aux sciences informatiques, pour créer un programme de qualité et des séances pratiques adaptées à chaque niveau.`}
            subtitleKey="about.partner.subtitle"
            reveal
          />

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-10">
            {PARTNERSHIP_ITEMS.map((item, i) => (
              <Reveal key={item.title} animation="fade-up" delay={i * 80}>
                <div className="text-center sm:text-left">
                  <IconBadge
                    icon={item.icon}
                    variant={i % 2 === 0 ? 'purple' : 'orange'}
                    size="md"
                    className="mb-4 mx-auto sm:mx-0"
                  />
                  <EditableText
                    editKey={`about.partner.${i + 1}.title`}
                    defaultValue={item.title}
                    as="h3"
                    className="text-slate-900 font-semibold text-lg mb-2"
                  />
                  <EditableText
                    editKey={`about.partner.${i + 1}.text`}
                    defaultValue={item.desc}
                    as="p"
                    multiline
                    className="text-slate-600 text-sm leading-relaxed"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="L'équipe"
            eyebrowKey="about.team.eyebrow"
            title="Des formateurs passionnés"
            titleKey="about.team.title"
            subtitle="Qualifiés, pédagogues et à l'écoute de chaque élève"
            subtitleKey="about.team.subtitle"
            reveal
          />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10">
            {[1, 2, 3, 4].map((i) => (
              <Reveal key={i} animation="fade-up" delay={i * 60}>
                <div className="text-center">
                  <div
                    className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-[rgba(58,0,224,0.12)] to-[rgba(250,176,58,0.2)] rounded-full mx-auto mb-4 ring-2 ring-[rgba(58,0,224,0.12)]"
                    aria-hidden="true"
                  />
                  <EditableText
                    editKey={`about.team.${i}.name`}
                    defaultValue={`Formateur ${i}`}
                    as="h4"
                    className="text-slate-900 font-semibold mb-1 text-sm sm:text-base"
                  />
                  <EditableText
                    editKey="about.team.role"
                    defaultValue="Expert robotique"
                    as="p"
                    className="text-slate-600 text-xs sm:text-sm"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        title={`Rejoignez ${brandName}`}
        titleKey="about.cta.title"
        subtitle="Offrez à votre enfant les compétences du futur dans un environnement stimulant et bienveillant"
        subtitleKey="about.cta.subtitle"
      >
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/reserver">
            <Button
              size="lg"
              className="w-full sm:w-auto bg-white text-[#3a00e0] hover:bg-[rgba(58,0,224,0.06)] shadow-lg"
            >
              <EditableText editKey="about.cta.primary" defaultValue="Réserver un atelier" as="span" />
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <Link to="/contact">
            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-white/80 text-white hover:bg-white/10"
            >
              <EditableText editKey="about.cta.secondary" defaultValue="Nous contacter" as="span" />
            </Button>
          </Link>
        </div>
      </CtaBanner>

      <Footer />
    </div>
  );
};

export default About;
